
import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Expense, ExpenseUser } from "@/types/expense";
import { useTranslation } from "react-i18next";

interface ProfileData {
  id: string;
  name: string | null;
  username: string | null;
  avatar_url: string | null;
}

interface ExpenseShare {
  profile_id: string;
  profiles: ProfileData;
}

interface ExpenseData {
  id: string;
  title: string;
  amount: number;
  currency: string;
  paid_by: string;
  created_at: string;
  jpy_amount: number | null;
  paid_by_profile: ProfileData;
  expense_shares: ExpenseShare[];
}

export const useGroupExpenses = (
  groupId: string | undefined, 
  onExpenseChange: () => void
) => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [editingExpenseId, setEditingExpenseId] = useState<string | null>(null);
  const { toast } = useToast();
  const { t } = useTranslation();
  
  const [expenseFormState, setExpenseFormState] = useState({
    title: "",
    amount: "",
    currency: "JPY",
    paidBy: "",
    sharedWith: [] as string[]
  });

  const fetchExpenses = useCallback(async () => {
    if (!groupId) return;
    
    console.log('Fetching expenses...');
    try {
      const { data: expensesData, error: expensesError } = await supabase
        .from('expenses')
        .select(`
          id,
          title,
          amount,
          currency,
          paid_by,
          created_at,
          jpy_amount,
          paid_by_profile:profiles!paid_by(id, name, username, avatar_url),
          expense_shares!inner (
            profile_id,
            profiles:profile_id(id, name, username, avatar_url)
          )
        `)
        .eq('group_id', groupId)
        .order('created_at', { ascending: false });
      
      if (expensesError) {
        console.error('Error fetching expenses:', expensesError);
        return;
      }
      
      if (!expensesData) {
        console.log('No expenses data returned');
        return;
      }
      
      console.log('Setting expenses:', expensesData);
      
      const formattedExpenses: Expense[] = expensesData.map(e => {
        // Cast to the correct type using type assertion
        const paidByProfile = e.paid_by_profile as unknown as ProfileData;

        const sharedWithUsers = Array.isArray(e.expense_shares) 
          ? e.expense_shares.map(s => {
              // Cast to the correct type
              const profile = s.profiles as unknown as ProfileData;
              return {
                id: profile.id,
                name: profile.name || 'Former Member',
                username: profile.username ? profile.username.split('@')[0] : 'removed',
                avatar_url: profile.avatar_url || ''
              } as ExpenseUser;
            })
          : [];

        const sharedWith = Array.isArray(e.expense_shares)
          ? e.expense_shares.map(s => s.profile_id)
          : [];

        return {
          id: e.id,
          title: e.title,
          amount: Number(e.amount) || 0,
          currency: e.currency,
          paidBy: e.paid_by,
          paidByUser: {
            id: paidByProfile.id,
            name: paidByProfile.name || 'Former Member',
            username: paidByProfile.username ? paidByProfile.username.split('@')[0] : 'removed',
            avatar_url: paidByProfile.avatar_url || ''
          },
          sharedWith,
          sharedWithUsers,
          date: new Date(e.created_at),
          jpy_amount: e.jpy_amount || Number(e.amount) || 0
        };
      });
      
      setExpenses(formattedExpenses);
      
      onExpenseChange();
    } catch (error) {
      console.error("Error in fetchExpenses:", error);
    }
  }, [groupId, onExpenseChange]);

  const handleExpenseSubmit = useCallback(async (formData: {
    title: string;
    amount: string;
    currency: string;
    paidBy: string;
    sharedWith: string[];
  }) => {
    if (!groupId) return;
    
    const amount = parseFloat(formData.amount);
    
    try {
      let jpyAmount = amount;
      
      if (formData.currency !== 'JPY') {
        const { data: rateData, error: rateError } = await supabase.functions.invoke('get-exchange-rate', {
          body: {
            fromCurrency: formData.currency,
            toCurrency: 'JPY'
          }
        });
        
        if (rateError) {
          console.error('Error getting exchange rate:', rateError);
          throw new Error('Failed to get exchange rate');
        }
        
        const rate = rateData?.rate || 1;
        jpyAmount = amount * rate;
      }
      
      const shareAmount = jpyAmount / formData.sharedWith.length;
      
      if (editingExpenseId) {
        const { error: expenseError } = await supabase
          .from('expenses')
          .update({
            title: formData.title,
            amount: amount,
            currency: formData.currency,
            paid_by: formData.paidBy,
            jpy_amount: jpyAmount
          })
          .eq('id', editingExpenseId);
        
        if (expenseError) {
          console.error('Error updating expense:', expenseError);
          throw new Error('Failed to update expense');
        }
        
        const { error: deleteSharesError } = await supabase
          .from('expense_shares')
          .delete()
          .eq('expense_id', editingExpenseId);
        
        if (deleteSharesError) {
          console.error('Error deleting shares:', deleteSharesError);
          throw new Error('Failed to update expense shares');
        }
        
        const shares = formData.sharedWith.map(memberId => ({
          expense_id: editingExpenseId,
          profile_id: memberId,
          share_amount: shareAmount
        }));
        
        for (const share of shares) {
          const { error: shareError } = await supabase
            .from('expense_shares')
            .insert(share);
            
          if (shareError) {
            console.error('Error inserting share:', shareError);
            throw new Error('Failed to insert expense share');
          }
        }
        
      } else {
        const { data: newExpense, error: expenseError } = await supabase
          .from('expenses')
          .insert({
            group_id: groupId,
            title: formData.title,
            amount: amount,
            currency: formData.currency,
            paid_by: formData.paidBy,
            jpy_amount: jpyAmount
          })
          .select()
          .single();
        
        if (expenseError || !newExpense) {
          console.error('Error creating expense:', expenseError);
          throw new Error('Failed to create expense');
        }
        
        const shares = formData.sharedWith.map(memberId => ({
          expense_id: newExpense.id,
          profile_id: memberId,
          share_amount: shareAmount
        }));
        
        for (const share of shares) {
          const { error: shareError } = await supabase
            .from('expense_shares')
            .insert(share);
            
          if (shareError) {
            console.error('Error inserting share:', shareError);
            throw new Error('Failed to insert expense share');
          }
        }
      }
      
      setShowExpenseForm(false);
      setEditingExpenseId(null);
      setExpenseFormState({
        title: "",
        amount: "",
        currency: "JPY",
        paidBy: "",
        sharedWith: []
      });

      onExpenseChange();

      toast({
        description: editingExpenseId 
          ? t('expenses.expenseUpdated', 'Expense updated successfully') 
          : t('expenses.expenseAdded', 'Expense added successfully'),
      });

      await fetchExpenses();
    } catch (error: any) {
      console.error('Error with expense:', error);
      toast({
        title: t('common.error'),
        description: error.message || t('expenses.saveFailed', 'Failed to save expense'),
        variant: "destructive"
      });
    }
  }, [groupId, editingExpenseId, toast, t, fetchExpenses, onExpenseChange]);

  const handleEditExpense = useCallback((expense: Expense) => {
    setEditingExpenseId(expense.id);
    setExpenseFormState({
      title: expense.title,
      amount: expense.amount.toString(),
      currency: expense.currency,
      paidBy: expense.paidBy,
      sharedWith: expense.sharedWith
    });
    setShowExpenseForm(true);
  }, []);

  const handleDeleteExpense = useCallback(async (expenseId: string) => {
    if (!groupId) return;
    
    try {
      const { error: shareDeleteError } = await supabase
        .from('expense_shares')
        .delete()
        .eq('expense_id', expenseId);
      
      if (shareDeleteError) {
        console.error('Error deleting expense shares:', shareDeleteError);
        throw new Error('Failed to delete expense shares');
      }
      
      const { error: expenseDeleteError } = await supabase
        .from('expenses')
        .delete()
        .eq('id', expenseId);
      
      if (expenseDeleteError) {
        console.error('Error deleting expense:', expenseDeleteError);
        throw new Error('Failed to delete expense');
      }
      
      toast({
        description: t('expenses.expenseDeleted', 'Expense deleted successfully'),
      });
      
      onExpenseChange();
      
      await fetchExpenses();
    } catch (error: any) {
      console.error('Error deleting expense:', error);
      toast({
        title: t('common.error'),
        description: error.message || t('expenses.deleteFailed', 'Failed to delete expense'),
        variant: "destructive"
      });
    }
  }, [groupId, toast, t, fetchExpenses, onExpenseChange]);

  return {
    expenses,
    showExpenseForm,
    editingExpenseId,
    expenseFormState,
    setShowExpenseForm,
    fetchExpenses,
    handleExpenseSubmit,
    handleEditExpense,
    handleDeleteExpense
  };
};
